import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react"
import {
  getCookie,
  setSessionCookie,
  deleteCookie,
  AUTH_ACCESS_TOKEN_COOKIE,
  AUTH_REFRESH_TOKEN_COOKIE,
  AUTH_USER_COOKIE,
} from "@/lib/cookies"
import type { EmailRegistrationRequest } from "@/types/emailRegistrationRequest"
import {
  dbApi,
  getApiErrorMessage,
  setAuthRef,
  doRefresh,
  clearRefreshFailure,
} from "@/lib/api"
import {
  identifyUser,
  resetAnalytics,
  trackEvent,
  trackSignIn,
  AnalyticsEvents,
} from "@/lib/analytics"
import { getAccessTokenExp } from "@/lib/jwt"

interface User {
  userId: string;
  email: string;
  displayName: string;
  orgId?: string;
  role?: "admin" | "user";
  termsAcceptedAt?: string | null;
  organization?: { orgId: string; name: string } | null;
}

interface Tokens {
  accessToken: string;
  refreshToken: string;
}

interface LoginResponse {
  message: string;
  user: User;
  tokens: Tokens;
}

// -----------

export type AuthUser = {
  id: string
  email: string
  name?: string
  role?: "admin" | "user"
  orgId?: string
  orgName?: string
  termsAcceptedAt?: string | null
}

function mapApiUserToAuthUser(user: User): AuthUser {
  return {
    id: user.userId,
    email: user.email,
    name: user.displayName ?? undefined,
    role: user.role,
    orgId: user.orgId,
    orgName: user.organization?.name,
    termsAcceptedAt: user.termsAcceptedAt ?? null,
  }
}

export type AuthCredentials = {
  email: string
  password: string
}

type AuthContextValue = {
  user: AuthUser | null
  isLoading: boolean
  error: string | null
  login: (credentials: AuthCredentials) => Promise<boolean>
  register: (
    payload: EmailRegistrationRequest
  ) => Promise<{ ok: true; message: string } | { ok: false; error: string }>
  acceptTerms: () => Promise<boolean>
  changePassword: (
    currentPassword: string,
    newPassword: string
  ) => Promise<{ ok: true; message: string } | { ok: false; error: string }>
  logout: () => void
  clearError: () => void
  getAccessToken: () => string | null
}

const AuthContext = createContext<AuthContextValue | null>(null)

function loadStoredUser(): AuthUser | null {
  try {
    const stored = getCookie(AUTH_USER_COOKIE)
    if (!stored) return null
    const parsed = JSON.parse(stored) as AuthUser
    if (parsed?.id && parsed?.email) return parsed
  } catch {
    // ignore
  }
  return null
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const persistAuthUser = useCallback((mappedUser: AuthUser) => {
    setSessionCookie(AUTH_USER_COOKIE, JSON.stringify(mappedUser))
    setUser(mappedUser)
  }, [])

  const checkAuth = useCallback(async () => {
    const token = getCookie(AUTH_ACCESS_TOKEN_COOKIE)
    if (!token) {
      setUser(null)
      setIsLoading(false)
      return
    }
    clearRefreshFailure()
    const storedUser = loadStoredUser()
    try {
      const { data } = await dbApi.get<{ user: User }>("/api/auth/me")
      const mappedUser = mapApiUserToAuthUser(data.user)
      setSessionCookie(AUTH_USER_COOKIE, JSON.stringify(mappedUser))
      identifyUser(mappedUser, "session_restore")
      setUser(mappedUser)
    } catch {
      if (storedUser) {
        identifyUser(storedUser, "session_restore")
        setUser(storedUser)
      } else {
        setUser(null)
      }
    }
    setIsLoading(false)
  }, [])

  useEffect(() => {
    void checkAuth()
  }, [checkAuth])

  const login = useCallback(async (credentials: AuthCredentials): Promise<boolean> => {
    setError(null)
    if (!credentials.email?.trim() || !credentials.password) {
      setError("Email and password are required")
      return false
    }
    try {
      const { data } = await dbApi.post<LoginResponse>("/api/auth/login", credentials)
      const mappedUser = mapApiUserToAuthUser(data.user)
      setSessionCookie(AUTH_ACCESS_TOKEN_COOKIE, data.tokens.accessToken)
      setSessionCookie(AUTH_REFRESH_TOKEN_COOKIE, data.tokens.refreshToken)
      persistAuthUser(mappedUser)
      identifyUser(mappedUser, "login")
      trackSignIn()
      clearRefreshFailure()
      return true
    } catch (e) {
      trackEvent(AnalyticsEvents.SignInFailed, {
        error: getApiErrorMessage(e),
      })
      setError(getApiErrorMessage(e))
      return false
    }
  }, [persistAuthUser])

  const register = useCallback(
    async (
      payload: EmailRegistrationRequest
    ): Promise<{ ok: true; message: string } | { ok: false; error: string }> => {
      if (!payload.email?.trim() || !payload.displayName?.trim()) {
        return { ok: false, error: "Name and email are required" }
      }
      try {
        // Account creation is deferred to the emailed verification link, so no
        // tokens are returned here — just a generic acknowledgement.
        const { data } = await dbApi.post<{ message: string }>(
          "/api/auth/register",
          payload
        )
        return { ok: true, message: data.message }
      } catch (e) {
        trackEvent(AnalyticsEvents.SignUpFailed, {
          error: getApiErrorMessage(e),
        })
        return { ok: false, error: getApiErrorMessage(e) }
      }
    },
    []
  )

  const acceptTerms = useCallback(async (): Promise<boolean> => {
    try {
      const { data } = await dbApi.post<{ user: User }>("/api/auth/accept-terms")
      const mappedUser = mapApiUserToAuthUser(data.user)
      persistAuthUser(mappedUser)
      return true
    } catch {
      return false
    }
  }, [persistAuthUser])

  const changePassword = useCallback(
    async (
      currentPassword: string,
      newPassword: string
    ): Promise<{ ok: true; message: string } | { ok: false; error: string }> => {
      try {
        const { data } = await dbApi.post<{ message: string; tokens: Tokens }>(
          "/api/auth/change-password",
          { currentPassword, newPassword }
        )
        // Backend rotates tokens (other sessions are revoked); keep this one
        // signed in by persisting the freshly issued pair.
        setSessionCookie(AUTH_ACCESS_TOKEN_COOKIE, data.tokens.accessToken)
        setSessionCookie(AUTH_REFRESH_TOKEN_COOKIE, data.tokens.refreshToken)
        clearRefreshFailure()
        return { ok: true, message: data.message }
      } catch (e) {
        return { ok: false, error: getApiErrorMessage(e) }
      }
    },
    []
  )

  const logout = useCallback(() => {
    resetAnalytics()
    deleteCookie(AUTH_ACCESS_TOKEN_COOKIE)
    deleteCookie(AUTH_REFRESH_TOKEN_COOKIE)
    deleteCookie(AUTH_USER_COOKIE)
    setUser(null)
    setError(null)
  }, [])

  const clearError = useCallback(() => setError(null), [])

  const getAccessToken = useCallback(() => getCookie(AUTH_ACCESS_TOKEN_COOKIE), [])

  useEffect(() => {
    setAuthRef(getAccessToken, logout)
  }, [getAccessToken, logout])

  useEffect(() => {
    if (!user) return
    let timeoutId: number | undefined
    const scheduleSilentRefresh = () => {
      const token = getAccessToken()
      if (!token) return
      const exp = getAccessTokenExp(token)
      if (exp == null) return
      const refreshAtMs = exp * 1000 - Date.now() - 60 * 1000
      if (refreshAtMs <= 0) {
        doRefresh().then((newToken) => {
          if (!newToken) logout()
          else scheduleSilentRefresh()
        })
        return
      }
      timeoutId = window.setTimeout(() => {
        doRefresh().then((newToken) => {
          if (!newToken) logout()
          else scheduleSilentRefresh()
        })
      }, refreshAtMs)
    }
    scheduleSilentRefresh()
    return () => {
      if (timeoutId != null) window.clearTimeout(timeoutId)
    }
  }, [user, getAccessToken, logout])

  const value: AuthContextValue = {
    user,
    isLoading,
    error,
    login,
    register,
    acceptTerms,
    changePassword,
    logout,
    clearError,
    getAccessToken,
  }

  return (
    <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
  )
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) {
    throw new Error("useAuth must be used within an AuthProvider")
  }
  return ctx
}
